import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal = ({ isOpen, onClose }: TermsModalProps) => {
  // Prevent background scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-3xl border border-white/10 bg-[#0a0f1d] shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5 bg-[#0a0f1d]">
              <div>
                <h2 className="text-2xl font-semibold text-white tracking-tight">Terms of Use & License</h2>
                <p className="text-xs text-[#2dd4bf] mt-1 uppercase tracking-widest font-medium">Last Updated: 20 May 2025</p>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 text-slate-400 hover:text-white transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
              <div className="text-slate-300 space-y-10 leading-relaxed">
                
                {/* Introduction */}
                <section>
                  <h3 className="text-xl font-semibold text-white mb-4">1. Introduction</h3>
                  <p>
                    These Terms of Use ("Terms") govern your use of <span className="text-white font-medium">CipherGenix (PTY) LTD's</span> website and any related services ("Services"). By accessing or using our Services, you agree to be bound by these Terms and our governing laws.
                  </p>
                </section>

                {/* IP Section */}
                <section className="p-6 rounded-2xl bg-[#2dd4bf]/5 border border-[#2dd4bf]/10">
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                    <span className="w-2 h-2 rounded-full bg-[#2dd4bf] mr-3 shadow-[0_0_8px_#2dd4bf]" />
                    2. Intellectual Property
                  </h3>
                  <p>
                    All intellectual property rights in the Services—including software, proprietary algorithms, and clinical documentation—are owned exclusively by CipherGenix (PTY) LTD. You are prohibited from reproducing, modifying, or distributing any part of the platform without explicit written consent.
                  </p>
                </section>

                {/* Usage Section */}
                <section>
                  <h3 className="text-xl font-semibold text-white mb-4">3. Integration and Use</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <span className="text-[#2dd4bf] mr-3 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#2dd4bf]" />
                      <span>Users must comply with all applicable local and international healthcare regulations.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#2dd4bf] mr-3 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#2dd4bf]" />
                      <span>Users are responsible for ensuring the secure handling of any data exchanged with CipherGenix endpoints.</span>
                    </li>
                  </ul>
                </section>

                {/* Warranty/Liability */}
                <section className="grid md:grid-cols-2 gap-8 py-4">
                  <div className="p-5 border-l border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-2 underline decoration-[#2dd4bf]/20">4. Liability & Warranty</h3>
                    <p className="text-sm">Services are provided <span className="text-white">"as is"</span>. CipherGenix (PTY) LTD disclaims all liability for damages arising from the use or inability to use the Services.</p>
                  </div>
                  <div className="p-5 border-l border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-2 underline decoration-[#2dd4bf]/20">5. Termination</h3>
                    <p className="text-sm">We reserve the right to suspend or terminate access to our infrastructure at any time, without notice, for breaches of security protocol.</p>
                  </div>
                </section>

                {/* License Section */}
                <section className="pt-8 border-t border-white/5">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-white mb-2">License Agreement</h2>
                    <div className="h-1 w-20 bg-gradient-to-r from-[#2dd4bf] to-blue-500 mx-auto rounded-full" />
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2">Grant of License</h4>
                      <p>CipherGenix (PTY) LTD grants you a non-exclusive, non-transferable license to utilize the Services solely for your internal business and clinical purposes.</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-white mb-2">Restrictions</h4>
                      <p>You may not sublicense, assign, reverse engineer, or transfer this license to any third party without prior written authorization.</p>
                    </div>
                  </div>
                </section>

                {/* Footer Info */}
                <div className="mt-12 p-8 rounded-3xl border border-white/10 bg-white/5 text-center">
                   <p className="text-slate-400 mb-2">Governed by the laws of the <span className="text-white">Republic of South Africa</span>.</p>
                   <p className="text-sm">For more info, visit our <a href="#security" onClick={onClose} className="text-[#2dd4bf] hover:underline">Data Protection</a> page.</p>
                </div>

                <p className="text-center text-slate-600 text-xs tracking-widest uppercase py-8">
                  End of Terms & License Agreement
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TermsModal;